/* Havaners Heat: rails, card effects, quick add and entrance motion for the hx- sections. */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Rails: arrow buttons and the progress bar ---------- */
function initRail(root) {
  const rail = root.querySelector('.hx-rail');
  if (!rail || rail.dataset.hxReady) return;
  rail.dataset.hxReady = '1';
  const prev = root.querySelector('.hx-rail-btn[data-dir="-1"]');
  const next = root.querySelector('.hx-rail-btn[data-dir="1"]');
  const bar = root.querySelector('.hx-rail-progress');

  const update = () => {
    const max = rail.scrollWidth - rail.clientWidth;
    const seen = rail.scrollWidth ? (rail.scrollLeft + rail.clientWidth) / rail.scrollWidth : 1;
    if (bar) {
      bar.style.setProperty('--hx-progress', Math.min(1, Math.max(0.08, seen)).toFixed(3));
      bar.hidden = max <= 2;
    }
    if (prev) prev.disabled = rail.scrollLeft <= 2;
    if (next) next.disabled = rail.scrollLeft >= max - 2;
  };

  [prev, next].forEach((btn) => {
    if (!btn) return;
    btn.addEventListener('click', () => {
      const dir = Number(btn.dataset.dir) || 1;
      const item = rail.querySelector('.hx-rail__item');
      const step = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).columnGap || 0) : rail.clientWidth;
      const per = Math.max(1, Math.floor((rail.clientWidth + 1) / step));
      rail.scrollBy({ left: dir * step * per, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  });

  rail.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
  window.addEventListener('resize', update);
  update();
}

/* ---------- Product cards: the second image opens from the cursor ---------- */
const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

// load the hover image, and only let it show once it has fully decoded
function loadAlt(card) {
  if (card.dataset.hxAlt) return;
  card.dataset.hxAlt = '1';
  const img = card.querySelector('.hx-card__img--alt');
  if (!img || !img.dataset.src) return;
  if (img.dataset.srcset) img.srcset = img.dataset.srcset;
  img.src = img.dataset.src;
  const ready = () => card.classList.add('is-ready');
  (img.decode ? img.decode() : Promise.reject()).then(ready, () => {
    if (img.complete && img.naturalWidth) ready();
    else img.addEventListener('load', ready, { once: true });
  });
}

const altObserver =
  canHover && 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => entries.forEach((e) => {
          if (!e.isIntersecting) return;
          altObserver.unobserve(e.target);
          loadAlt(e.target);
        }),
        { rootMargin: '300px' }
      )
    : null;

function initCardPointer(scope) {
  if (!canHover) return;
  scope.querySelectorAll('.hx-card--swap .hx-card__media').forEach((media) => {
    if (media.dataset.hxReady) return;
    media.dataset.hxReady = '1';
    const card = media.closest('.hx-card');
    if (altObserver) altObserver.observe(card);
    else loadAlt(card);
    media.addEventListener('pointerenter', () => loadAlt(card), { once: true });
    media.addEventListener('pointerenter', (e) => {
      const r = media.getBoundingClientRect();
      media.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
      media.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
    });
  });
}

/* ---------- Quick add: stays on the page and updates the bag ---------- */
let events = null;
async function getEvents() {
  if (events !== null) return events;
  try {
    events = await import('@shopify/events');
  } catch (err) {
    events = false;
  }
  return events;
}

function initQuickAdd(scope) {
  scope.querySelectorAll('form.hx-card__add').forEach((form) => {
    if (form.dataset.hxReady) return;
    form.dataset.hxReady = '1';
    form.addEventListener('submit', async (e) => {
      if (!window.fetch || !window.Theme?.routes?.cart_add_url) return;
      e.preventDefault();
      const btn = form.querySelector('.hx-card__add-btn');
      const label = form.querySelector('.hx-card__add-label');
      btn?.setAttribute('aria-busy', 'true');

      const data = new FormData(form);
      const ids = [...document.querySelectorAll('cart-items-component')]
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
      if (ids.length) {
        data.append('sections', ids.join(','));
        data.append('sections_url', window.location.pathname);
      }

      const ev = await getEvents();
      const deferred = ev?.CartLinesUpdateEvent?.createPromise?.();
      if (ev && deferred) {
        form.dispatchEvent(
          new ev.CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: String(data.get('id')), quantity: 1 }],
            promise: deferred.promise,
          })
        );
      }

      try {
        const res = await fetch(window.Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data,
        }).then((r) => r.json());
        if (res.status) throw new Error(res.description || res.message || 'Could not add to bag');

        const cart = await fetch(`${window.Theme.routes.cart_url}.json`).then((r) => r.json());
        if (deferred) {
          deferred.resolve({
            cart: ev.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items,
              source: 'product-form-component',
              sourceId: 'hx-quick-add',
              itemCount: 1,
              productId: String(res.product_id || ''),
              sections: res.sections,
              didError: false,
            },
          });
        }
        if (btn && label) {
          const text = label.dataset.text || (label.dataset.text = label.textContent);
          btn.classList.add('is-added');
          label.textContent = 'Added';
          setTimeout(() => {
            btn.classList.remove('is-added');
            label.textContent = text;
          }, 2200);
        }
      } catch (err) {
        deferred?.reject?.(err);
        form.submit();
      } finally {
        btn?.removeAttribute('aria-busy');
      }
    });
  });
}

/* ---------- Lens compare: drag (or arrow keys) to wipe between the two shots ---------- */
function initCompare(scope) {
  scope.querySelectorAll('[data-hx-compare]').forEach((fig) => {
    if (fig.dataset.hxReady) return;
    fig.dataset.hxReady = '1';
    const range = fig.querySelector('.hx-compare__range');
    if (!range) return;
    const set = () => fig.style.setProperty('--pos', `${range.value}%`);
    range.addEventListener('input', set);
    // pointer drag anywhere on the image (iOS won't move an invisible range thumb on tap)
    let dragging = false;
    const move = (e) => {
      const r = fig.getBoundingClientRect();
      range.value = String(Math.round(Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100))));
      set();
    };
    fig.addEventListener('pointerdown', (e) => {
      dragging = true;
      fig.setPointerCapture?.(e.pointerId);
      move(e);
    });
    fig.addEventListener('pointermove', (e) => { if (dragging) move(e); });
    ['pointerup', 'pointercancel'].forEach((t) => fig.addEventListener(t, () => { dragging = false; }));
    set();
  });
}

/* ---------- Shop by shape: a WebGL spotlight brings the colour back wherever you look ---------- */
function whenThree(fn) {
  if (window.THREE) return fn();
  // the home hero loads Three.js after the page has loaded and tells us when it is ready
  let done = false;
  window.addEventListener('hx:three', () => { if (!done) { done = true; fn(); } }, { once: true });
  let tries = 0;
  const t = setInterval(() => {
    if (done) clearInterval(t);
    else if (window.THREE) {
      clearInterval(t);
      done = true;
      fn();
    } else if (++tries > 40) clearInterval(t);
  }, 250);
}

function tiltLook(el) {
  el.addEventListener('pointermove', (e) => {
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.setProperty('--ty', `${(x * 6).toFixed(2)}deg`);
    el.style.setProperty('--tx', `${(-y * 6).toFixed(2)}deg`);
  });
  el.addEventListener('pointerleave', () => {
    el.style.setProperty('--tx', '0deg');
    el.style.setProperty('--ty', '0deg');
  });
}

const LOOK_FRAG = `precision highp float;
uniform sampler2D uTex; uniform vec2 uRes; uniform vec2 uCover; uniform vec2 uSpot; uniform float uR; uniform float uTime; uniform float uIn;
varying vec2 vUv;
float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
void main(){
  vec2 uv = (vUv - 0.5) * uCover + 0.5;
  vec3 real = texture2D(uTex, uv).rgb;
  float l = dot(real, vec3(0.299, 0.587, 0.114));
  vec3 duo = mix(vec3(0.122, 0.125, 0.137), vec3(0.98, 0.93, 0.86), smoothstep(0.04, 0.96, l));
  vec2 px = vec2(vUv.x, 1.0 - vUv.y) * uRes;
  float spot = (1.0 - smoothstep(uR * 0.5, uR, distance(px, uSpot))) * uIn;
  vec3 col = mix(duo, real * 1.04, spot);
  float band = smoothstep(0.05, 0.0, abs(fract((vUv.x + vUv.y) * 0.5 - uTime * 0.06) - 0.5));
  col += vec3(1.0, 0.95, 0.88) * band * 0.07;
  col += (hash(px + fract(uTime) * 91.0) - 0.5) * 0.05;
  gl_FragColor = vec4(col, 1.0);
}`;
const LOOK_VERT = 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }';

function spotlight(el, phase) {
  const THREE = window.THREE;
  const canvas = el.querySelector('.hx-look__gl');
  const img = el.querySelector('.hx-look__img');
  const media = el.querySelector('.hx-look__media');
  if (!canvas || !img || !media) return;
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false, powerPreference: 'low-power' });
  } catch (err) {
    return;
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
  const uniforms = {
    uTex: { value: null }, uRes: { value: new THREE.Vector2(1, 1) }, uCover: { value: new THREE.Vector2(1, 1) },
    uSpot: { value: new THREE.Vector2(0, 0) }, uR: { value: 100 }, uTime: { value: 0 }, uIn: { value: 0 },
  };
  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.ShaderMaterial({ uniforms, vertexShader: LOOK_VERT, fragmentShader: LOOK_FRAG })));

  let w = 1, h = 1, hovering = false, visible = true, raf = 0, imgAspect = 1;
  const spot = { x: 0, y: 0 }, target = { x: 0, y: 0 };
  const t0 = performance.now();

  const layout = () => {
    w = Math.max(1, media.offsetWidth);
    h = Math.max(1, media.offsetHeight);
    renderer.setSize(w, h, false);
    uniforms.uRes.value.set(w, h);
    const a = w / h;
    uniforms.uCover.value.set(a < imgAspect ? a / imgAspect : 1, a < imgAspect ? 1 : imgAspect / a);
    uniforms.uR.value = Math.max(70, Math.min(w, h) * 0.4);
  };

  const loop = (now) => {
    raf = 0;
    if (!visible) return;
    const t = (now - t0) / 1000;
    uniforms.uTime.value = t;
    uniforms.uIn.value += (1 - uniforms.uIn.value) * 0.04;
    if (!hovering) {
      // drift on its own; on phones it also follows the tile up the screen as you scroll
      const r = el.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, (window.innerHeight - r.top) / (window.innerHeight + r.height)));
      target.x = w * (0.5 + 0.26 * Math.sin(t * 0.5 + phase));
      target.y = h * (canHover ? 0.38 + 0.1 * Math.cos(t * 0.4 + phase) : 0.15 + 0.55 * p);
    }
    spot.x += (target.x - spot.x) * 0.1;
    spot.y += (target.y - spot.y) * 0.1;
    uniforms.uSpot.value.set(spot.x, spot.y);
    renderer.render(scene, camera);
    raf = requestAnimationFrame(loop);
  };

  new THREE.TextureLoader().load(img.currentSrc || img.src, (tex) => {
    tex.minFilter = THREE.LinearFilter;
    if (tex.image && tex.image.width) imgAspect = tex.image.width / tex.image.height;
    uniforms.uTex.value = tex;
    layout();
    spot.x = target.x = w / 2;
    spot.y = target.y = h * 0.4;
    el.classList.add('is-gl');
    raf = requestAnimationFrame(loop);
  });

  el.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse') return;
    const r = media.getBoundingClientRect();
    hovering = true;
    target.x = ((e.clientX - r.left) / r.width) * w;
    target.y = ((e.clientY - r.top) / r.height) * h;
  });
  el.addEventListener('pointerleave', () => { hovering = false; });
  if ('ResizeObserver' in window) new ResizeObserver(() => { if (uniforms.uTex.value) layout(); }).observe(media);
  new IntersectionObserver((entries) => {
    visible = entries[0].isIntersecting;
    if (visible && !raf && uniforms.uTex.value) raf = requestAnimationFrame(loop);
  }).observe(el);
}

function initLooks(scope) {
  const looks = [...scope.querySelectorAll('[data-hx-look]')].filter((el) => !el.dataset.hxReady);
  if (!looks.length) return;
  looks.forEach((el) => { el.dataset.hxReady = '1'; });
  if (reduceMotion) return;
  if (canHover) looks.forEach(tiltLook);
  if (!('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(
    (entries) => entries.forEach((e) => {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      whenThree(() => spotlight(e.target, looks.indexOf(e.target) * 2.1));
    }),
    { rootMargin: '200px' }
  );
  looks.forEach((el) => io.observe(el));
}

/* ---------- Entrance motion ---------- */
function initReveal(scope) {
  if (reduceMotion || !('IntersectionObserver' in window)) return;
  const groups = scope.querySelectorAll('.hx-section:not(.hx-section--hero)');
  if (!groups.length) return;
  document.documentElement.classList.add('hx-anim');
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.querySelectorAll('[data-hx-in]').forEach((el) => el.classList.add('is-in'));
        io.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -12% 0px' }
  );
  groups.forEach((section) => {
    const items = section.querySelectorAll(
      '.hx-head, .hx-rail__item, .hx-campaign__media, .hx-campaign__copy, .hx-trust__item, .hx-faq__intro, .hx-q, .hx-lens__copy, .hx-compare, .hx-signup__media, .hx-signup__body'
    );
    items.forEach((el, i) => {
      el.setAttribute('data-hx-in', '');
      el.style.setProperty('--hx-i', String(Math.min(i, 6)));
    });
    io.observe(section);
  });
  // never leave anything hidden if the observer misses a section
  setTimeout(() => scope.querySelectorAll('[data-hx-in]').forEach((el) => el.classList.add('is-in')), 6000);
}

/* ---------- Palms: soft palm shadows that sway, drawn by one shared WebGL shader ----------
   Each frond bends on its own slow noise and the trunk leans with the breeze. The shadow
   softens towards the frond tips like a real one. One small GL canvas renders every palm and
   is copied into each visible spot, so it costs one context. Static CSS palm if no WebGL or
   reduced motion. */
const PALM_SPOTS = [
  { sel: '.hx-section--orange', key: 'sun', col: [0.122, 0.125, 0.137], a: 0.11, seed: 1.3 },
  { sel: '.hx-section--foot', key: 'foot', col: [1.0, 0.894, 0.722], a: 0.06, seed: 4.1 },
  { sel: '.hx-section:has(> .hx-lens), .hx-section:has(.hx-lens)', key: 'lens', col: [1.0, 0.894, 0.722], a: 0.05, seed: 7.7 }
];

function initPalms() {
  if (reduceMotion || !('IntersectionObserver' in window)) return;
  const hosts = [];
  PALM_SPOTS.forEach((spot) => {
    let els = [];
    try { els = [...document.querySelectorAll(spot.sel)]; } catch (e) { return; }
    els.forEach((el) => {
      if (el.querySelector(':scope > .hx-palmgl')) return;
      hosts.push({ el, spot });
    });
  });
  if (!hosts.length) return;

  const raw = getComputedStyle(document.documentElement).getPropertyValue('--hx-palm');
  const m = raw && raw.match(/url\(["']?(.+?)["']?\)\s*$/);
  if (!m) return;

  const W = 240, H = 384;
  const glc = document.createElement('canvas');
  glc.width = W; glc.height = H;
  const gl = glc.getContext('webgl', { premultipliedAlpha: false, preserveDrawingBuffer: true, antialias: false });
  if (!gl) return;

  const vs = 'attribute vec2 p; varying vec2 vUv; void main(){ vUv = p * 0.5 + 0.5; gl_Position = vec4(p, 0.0, 1.0); }';
  const fs = [
    'precision mediump float;',
    'uniform sampler2D uTex; uniform float uT; uniform float uSeed; uniform vec3 uCol; uniform float uA;',
    'varying vec2 vUv;',
    'float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }',
    'float noise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f * f * (3. - 2. * f); return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), f.x), f.y); }',
    'const vec2 crown = vec2(0.575, 0.25);',
    'vec2 sway(vec2 p){',
    '  float h = 1.0 - p.y;',
    '  float breeze = sin(uT * 0.31 + uSeed) * 0.6 + (noise(vec2(uT * 0.18, uSeed)) - 0.5) * 0.8;',
    '  p.x -= breeze * 0.016 * h * h;',
    '  vec2 d = p - crown; float r = length(d); float ang = atan(d.y, d.x);',
    '  float trunkX = 0.565 - (p.y - 0.29) * 0.27;',
    '  float frond = 1.0 - (1.0 - smoothstep(0.015, 0.06, abs(p.x - trunkX))) * smoothstep(0.26, 0.33, p.y);',
    '  float w = ((noise(vec2(ang * 2.2 + uSeed, uT * 0.42)) - 0.5) * 0.16 + breeze * 0.035) * smoothstep(0.03, 0.4, r) * frond;',
    '  float c = cos(w), s = sin(w);',
    '  d = mat2(c, -s, s, c) * d;',
    '  d.y -= sin(uT * 0.7 + ang * 3.0 + uSeed) * 0.006 * r * frond;',
    '  return crown + d;',
    '}',
    'void main(){',
    '  vec2 p = sway(vec2(vUv.x, 1.0 - vUv.y));',
    '  float rr = 0.003 + 0.011 * smoothstep(0.05, 0.45, distance(p, crown));',
    '  float a = texture2D(uTex, p).a * 0.2;',
    '  a += texture2D(uTex, p + vec2(rr, 0.0)).a * 0.1 + texture2D(uTex, p - vec2(rr, 0.0)).a * 0.1;',
    '  a += texture2D(uTex, p + vec2(0.0, rr)).a * 0.1 + texture2D(uTex, p - vec2(0.0, rr)).a * 0.1;',
    '  a += texture2D(uTex, p + vec2(rr, rr) * 0.7).a * 0.1 + texture2D(uTex, p - vec2(rr, rr) * 0.7).a * 0.1;',
    '  a += texture2D(uTex, p + vec2(rr, -rr) * 0.7).a * 0.1 + texture2D(uTex, p - vec2(rr, -rr) * 0.7).a * 0.1;',
    '  a *= 0.82 + 0.18 * noise(p * 18.0 + vec2(uT * 0.25, 0.0));',
    '  gl_FragColor = vec4(uCol, clamp(a, 0.0, 1.0) * uA);',
    '}'
  ].join('\n');
  const sh = (type, src) => { const o = gl.createShader(type); gl.shaderSource(o, src); gl.compileShader(o); return gl.getShaderParameter(o, gl.COMPILE_STATUS) ? o : null; };
  const v = sh(gl.VERTEX_SHADER, vs), f = sh(gl.FRAGMENT_SHADER, fs);
  if (!v || !f) return;
  const prog = gl.createProgram();
  gl.attachShader(prog, v); gl.attachShader(prog, f); gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
  gl.useProgram(prog);
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  const u = (n) => gl.getUniformLocation(prog, n);
  const uT = u('uT'), uSeed = u('uSeed'), uCol = u('uCol'), uA = u('uA');
  gl.viewport(0, 0, W, H);

  const img = new Image();
  img.onload = () => {
    const tc = document.createElement('canvas');
    tc.width = 400; tc.height = 640;
    tc.getContext('2d').drawImage(img, 0, 0, 400, 640);
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, tc);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    start();
  };
  img.src = m[1];

  function start() {
    const live = new Set();
    const items = hosts.map(({ el, spot }) => {
      const c = document.createElement('canvas');
      c.className = 'hx-palmgl';
      c.dataset.spot = spot.key;
      c.setAttribute('aria-hidden', 'true');
      el.prepend(c);
      el.classList.add('is-palm-live');
      return { el, spot, c, ctx: c.getContext('2d') };
    });
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const it = items.find((i) => i.el === e.target);
        if (e.isIntersecting) live.add(it); else live.delete(it);
      });
      if (live.size) loop();
    }, { rootMargin: '100px 0px' });
    items.forEach((it) => io.observe(it.el));

    let raf = 0, last = 0;
    const t0 = performance.now();
    function frame(now) {
      raf = 0;
      if (!live.size || document.hidden) return;
      raf = requestAnimationFrame(frame);
      if (now - last < 33) return;
      last = now;
      const t = (now - t0) / 1000;
      live.forEach((it) => {
        const r = it.c.getBoundingClientRect();
        const w = Math.max(1, Math.round(r.width)), h = Math.max(1, Math.round(r.height));
        if (it.c.width !== w || it.c.height !== h) { it.c.width = w; it.c.height = h; }
        gl.uniform1f(uT, t);
        gl.uniform1f(uSeed, it.spot.seed);
        gl.uniform3fv(uCol, it.spot.col);
        gl.uniform1f(uA, 1.0);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        it.ctx.clearRect(0, 0, w, h);
        it.ctx.globalAlpha = it.spot.a;
        it.ctx.drawImage(glc, 0, 0, w, h);
      });
    }
    function loop() { if (!raf) raf = requestAnimationFrame(frame); }
    document.addEventListener('visibilitychange', () => { if (!document.hidden && live.size) loop(); });
  }
}

function init(scope = document) {
  scope.querySelectorAll('[data-hx-rail]').forEach(initRail);
  initCardPointer(scope);
  initQuickAdd(scope);
  initCompare(scope);
  initLooks(scope);
  initReveal(scope);
}

init();
// palms are pure decoration, so they wait until the page is idle
(window.requestIdleCallback || ((fn) => setTimeout(fn, 1200)))(initPalms);

// collection filters, pagination and recommendations swap cards in after load
if ('MutationObserver' in window) {
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      initCardPointer(document);
      initQuickAdd(document);
    });
  }).observe(document.body, { childList: true, subtree: true });
}
// keep working in the theme editor when sections are added or changed
document.addEventListener('shopify:section:load', (e) => init(e.target));
